import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Card, Button, Input, Select, Tag, Form, Typography, Space,
  Checkbox, Alert, Row, Col, App as AntApp, Flex, Collapse,
} from 'antd';
import {
  ApiOutlined,
} from '@ant-design/icons';
import { API_URL } from '../../config';
import { getToken } from './utils';

const { Title, Text } = Typography;

// Exportado pra ser embedado como tab dentro de AdminIntegracoes (Sprint reorg
// onde D4 Sign passou a ser uma sub-aba de Integrações em vez de página própria).
export default function AdminD4Sign() {
  const { message } = AntApp.useApp();
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [testando, setTestando] = useState(false);
  const [listandoCofres, setListandoCofres] = useState(false);
  const [cofres, setCofres] = useState<Array<{ uuid: string; nome: string }>>([]);
  const [testeE2E, setTesteE2E] = useState<any>(null);
  const [cfg, setCfg] = useState({
    token_api: '', crypt_key: '', cofre_uuid: '',
    base_url: 'https://secure.d4sign.com.br/api/v1',
    webhook_secret: '', webhook_url: '',
    ativo: false, configurado: false,
  });

  useEffect(() => { carregar(); /* eslint-disable-line */ }, []);

  async function carregar() {
    setCarregando(true);
    try {
      const token = await getToken();
      const r = await axios.get(`${API_URL}/comercial/propostas-geradas/d4sign-config`,
        { headers: { Authorization: token } });
      const data = r.data.body ? JSON.parse(r.data.body) : r.data;
      setCfg({
        token_api: data.token_api || '', crypt_key: data.crypt_key || '',
        cofre_uuid: data.cofre_uuid || '',
        base_url: data.base_url || 'https://secure.d4sign.com.br/api/v1',
        webhook_secret: data.webhook_secret || '', webhook_url: data.webhook_url || '',
        ativo: !!data.ativo, configurado: !!data.configurado,
      });
    } catch (e: any) {
      message.error('Erro ao carregar: ' + (e.response?.data?.error || e.message));
    } finally { setCarregando(false); }
  }

  async function salvar() {
    setSalvando(true);
    try {
      const token = await getToken();
      await axios.put(`${API_URL}/comercial/propostas-geradas/d4sign-config`,
        { ...cfg, salvar_config: true }, { headers: { Authorization: token } });
      message.success('Configuração salva.');
      await carregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  async function testar() {
    setTestando(true);
    try {
      const token = await getToken();
      const r = await axios.put(`${API_URL}/comercial/propostas-geradas/d4sign-config`,
        { testar_config: true }, { headers: { Authorization: token } });
      const data = r.data.body ? JSON.parse(r.data.body) : r.data;
      const linhas = [`✓ Conexão OK`, `Endpoint: /${data.endpoint_validado}`, '',
        `Resposta:`, JSON.stringify(data.resposta, null, 2).slice(0, 600)];
      alert(linhas.join('\n'));
    } catch (e: any) {
      message.error('Falhou: ' + (e.response?.data?.error || e.message));
    } finally { setTestando(false); }
  }

  async function testarEnvioE2E() {
    const email = window.prompt(
      'Teste end-to-end D4 Sign\n\nDigite o e-mail que vai receber o convite:', ''
    );
    if (!email || !email.includes('@')) return;
    setTesteE2E(null);
    try {
      const token = await getToken();
      const r = await axios.put(`${API_URL}/comercial/propostas-geradas/d4sign-config`,
        { testar_envio_completo: true, email },
        { headers: { Authorization: token }, timeout: 90000 });
      const data = r.data.body ? JSON.parse(r.data.body) : r.data;
      setTesteE2E(data);
    } catch (e: any) {
      setTesteE2E({ sucesso: false, falhou_em: 'request', log: { erro: e.response?.data?.error || e.message } });
    }
  }

  async function listarCofres() {
    if (!cfg.token_api.trim() || !cfg.crypt_key.trim()) {
      message.warning('Preencha e salve Token API + Crypt Key antes.');
      return;
    }
    setListandoCofres(true);
    try {
      const token = await getToken();
      await axios.put(`${API_URL}/comercial/propostas-geradas/d4sign-config`,
        { ...cfg, salvar_config: true }, { headers: { Authorization: token } });
      const r = await axios.put(`${API_URL}/comercial/propostas-geradas/d4sign-config`,
        { listar_cofres: true }, { headers: { Authorization: token }, timeout: 25000 });
      const data = r.data.body ? JSON.parse(r.data.body) : r.data;
      const lista = Array.isArray(data.cofres) ? data.cofres : [];
      setCofres(lista);
      if (lista.length === 0) message.info('Nenhum cofre encontrado.');
      else if (lista.length === 1) setCfg(c => ({ ...c, cofre_uuid: lista[0].uuid }));
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setListandoCofres(false); }
  }

  if (carregando) return <Card loading />;

  return (
    <div style={{ maxWidth: 720 }}>
      <Flex align="center" gap={10} style={{ marginBottom: 14 }}>
        <Title level={5} style={{ margin: 0, color: '#020D7F' }}>D4 Sign — Integração de Assinatura Digital</Title>
        {cfg.configurado
          ? <Tag color="success">✓ Configurado</Tag>
          : <Tag color="warning">⚠ Falta configurar</Tag>}
      </Flex>
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 18 }}>
        Credenciais da API D4 Sign. Docs:{' '}
        <a href="https://docapi.d4sign.com.br/docs" target="_blank" rel="noopener noreferrer" style={{ color: '#0930D0' }}>
          docapi.d4sign.com.br
        </a>
      </Text>

      <Form layout="vertical" size="small">
        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item label="Token API" required extra="Painel D4 Sign → Configurações → API">
              <Input.Password value={cfg.token_api} onChange={e => setCfg(c => ({ ...c, token_api: e.target.value }))} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Crypt Key" required extra="Chave de criptografia (mesma tela do token).">
              <Input.Password value={cfg.crypt_key} onChange={e => setCfg(c => ({ ...c, crypt_key: e.target.value }))} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Cofre" required
              extra={cofres.length > 0 ? `${cofres.length} cofre(s) listado(s).` : 'Salve token + crypt key e clique "Listar".'}>
              <Space.Compact style={{ width: '100%' }}>
                {cofres.length > 0
                  ? <Select value={cfg.cofre_uuid} onChange={(v) => setCfg(c => ({ ...c, cofre_uuid: v }))}
                      style={{ flex: 1 }} placeholder="Escolha o cofre"
                      options={cofres.map(c => ({ label: c.nome, value: c.uuid }))} />
                  : <Input value={cfg.cofre_uuid} onChange={e => setCfg(c => ({ ...c, cofre_uuid: e.target.value }))}
                      style={{ flex: 1 }} placeholder="ex.: 12345678-1234-..." />}
                <Button onClick={listarCofres} loading={listandoCofres} type="primary" ghost>🗂 Listar</Button>
              </Space.Compact>
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Base URL" extra="Padrão: secure.d4sign.com.br (sandbox.d4sign.com.br p/ testes)">
              <Input value={cfg.base_url} onChange={e => setCfg(c => ({ ...c, base_url: e.target.value }))} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Webhook Secret (HMAC)" extra="Para validar callbacks. Use quando ativar o webhook.">
              <Input.Password value={cfg.webhook_secret} onChange={e => setCfg(c => ({ ...c, webhook_secret: e.target.value }))} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Webhook URL (somente leitura)" extra="URL pública pra callbacks (em breve).">
              <Input value={cfg.webhook_url} readOnly style={{ background: '#f6f6f8' }} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Checkbox checked={cfg.ativo} onChange={e => setCfg(c => ({ ...c, ativo: e.target.checked }))}>
              Integração ativa (botões "Enviar D4 Sign" disponíveis nas propostas)
            </Checkbox>
          </Col>
        </Row>
      </Form>

      <Flex gap={10} justify="flex-end" wrap="wrap" style={{ marginTop: 20 }}>
        <Button disabled={!cfg.configurado} onClick={testarEnvioE2E}
          style={{ color: '#B07500', borderColor: '#B07500' }}>🧪 Testar envio E2E</Button>
        <Button disabled={!cfg.configurado} loading={testando} onClick={testar} icon={<ApiOutlined />}
          type="primary" ghost>Testar conexão</Button>
        <Button type="primary" loading={salvando} onClick={salvar}>Salvar</Button>
      </Flex>

      {testeE2E && (
        <Alert
          type={testeE2E.sucesso ? 'success' : 'error'} showIcon style={{ marginTop: 16 }}
          message={testeE2E.sucesso
            ? `Teste E2E OK — Documento ${testeE2E.documento_uuid?.slice(0, 8)}…`
            : `Teste E2E falhou na etapa: ${testeE2E.falhou_em}`}
          description={
            <div>
              {testeE2E.mensagem && <Text style={{ fontSize: 12 }}>{testeE2E.mensagem}</Text>}
              <Collapse size="small" ghost style={{ marginTop: 8 }}
                items={[{
                  key: 'l', label: <Text style={{ fontSize: 11 }}>🔍 Ver log completo ({testeE2E.log?.etapas?.length || 0} etapas)</Text>,
                  children: <pre style={{ fontSize: 10, padding: 8, background: '#fff', maxHeight: 400, overflow: 'auto' }}>
                    {JSON.stringify(testeE2E.log, null, 2)}
                  </pre>,
                }]} />
            </div>
          }
        />
      )}
    </div>
  );
}
